import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { StargraveActiveBuff } from '../../types';
import { useStargraveBuffs } from '../../stores/stargraveCustomStore';
import { formatStargraveBuffEffect, getStargraveBuffById } from '../../game/stargraveBuffRules';

interface StargraveManageBuffsModalProps {
  open: boolean;
  memberName: string;
  activeBuffs: StargraveActiveBuff[];
  onClose: () => void;
  onAdd: (buffId: string) => void;
}

export const StargraveManageBuffsModal: React.FC<StargraveManageBuffsModalProps> = ({
  open,
  memberName,
  activeBuffs,
  onClose,
  onAdd,
}) => {
  const [selectedBuffId, setSelectedBuffId] = React.useState<string | null>(null);
  const stargraveBuffs = useStargraveBuffs();

  React.useEffect(() => {
    if (open) setSelectedBuffId(null);
  }, [open]);

  const availableBuffs = stargraveBuffs.filter((buff) => {
    const active = activeBuffs.find((candidate) => candidate.buffId === buff.id);
    return !active || active.stackCount < (buff.stacking ?? 1);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add buff / debuff — {memberName}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <List dense>
          {availableBuffs.map((buff) => (
            <ListItemButton
              key={buff.id}
              selected={selectedBuffId === buff.id}
              onClick={() => setSelectedBuffId(buff.id)}
            >
              <ListItemText primary={buff.name} secondary={formatStargraveBuffEffect(buff)} />
            </ListItemButton>
          ))}
        </List>
        {selectedBuffId && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
            {getStargraveBuffById(selectedBuffId)?.summary}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedBuffId}
          onClick={() => {
            if (selectedBuffId) onAdd(selectedBuffId);
            onClose();
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
